import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";

import { useMkRoot } from "@/app/branding";

const OPEN_MODALS: symbol[] = [];

interface Props {
    open: boolean;
    title?: string;
    subtitle?: string;
    icon?: string;
    size?: "sm" | "md" | "lg" | "xl" | "full";
    closeOnScrim?: boolean;
    header?: ReactNode;
    footer?: ReactNode;
    onClose(): void;
    children: ReactNode;
}

export function MkModal({ open, title, subtitle, icon, size = "md", closeOnScrim = true,
                          header, footer, onClose, children }: Props) {
    const root = useMkRoot();

    const closeRef = useRef(onClose);
    closeRef.current = onClose;

    useEffect(() => {
        if (!open) {
            return;
        }
        const token = Symbol("modal");
        OPEN_MODALS.push(token);
        function onKey(event: KeyboardEvent) {
            if (event.key === "Escape" && !event.defaultPrevented
                    && OPEN_MODALS[OPEN_MODALS.length - 1] === token) {
                event.preventDefault();
                closeRef.current();
            }
        }
        window.addEventListener("keydown", onKey);
        return () => {
            window.removeEventListener("keydown", onKey);
            const index = OPEN_MODALS.indexOf(token);
            if (index >= 0) {
                OPEN_MODALS.splice(index, 1);
            }
        };
    }, [open]);

    if (!open) {
        return null;
    }

    return createPortal(
        <div className="mk mk-modal-root" {...root}>
            <div className="mk-modal-scrim" onClick={() => {
                if (closeOnScrim) {
                    onClose();
                }
            }} />
            <div className={`mk-modal mk-modal-${size}`} role="dialog" aria-modal="true">
                {(title || header) && (
                    <div className="mk-modal-header">
                        {header ?? (
                            <>
                                {icon && (
                                    <span className="mk-modal-icon">
                                        <i className={`fa ${icon}`} />
                                    </span>
                                )}
                                <div className="mk-modal-heading">
                                    <div className="mk-modal-title">{title}</div>
                                    {subtitle && (
                                        <div className="mk-modal-subtitle">{subtitle}</div>
                                    )}
                                </div>
                            </>
                        )}
                        <button className="mk-modal-close" onClick={onClose}>
                            <i className="fa fa-times" />
                        </button>
                    </div>
                )}
                <div className="mk-modal-body">{children}</div>
                {footer && <div className="mk-modal-footer">{footer}</div>}
            </div>
        </div>,
        document.body,
    );
}
